import type { JsonObject, JsonPath, JsonValue } from "../types";

export interface ContentWarning {
  path: string;
  term: string;
  excerpt: string;
}

export const PROHIBITED_TERMS = [
  "guaranteed",
  "guarantee",
  "certified",
  "authorized distributor",
  "official distributor",
  "market leader",
  "lowest price",
  "cheapest",
  "garantili",
  "garanti ediyoruz",
  "sertifikalı",
  "yetkili distribütör",
  "resmi distribütör",
  "pazar lideri",
  "en düşük fiyat",
  "en ucuz"
] as const;

// `companyName` is also the inquiry form's localized field label, so it must
// remain editable. Only unambiguous identity keys are locked.
const BRAND_KEYS = new Set(["brand", "brandname"]);

export function isPlainObject(value: unknown): value is JsonObject {
  return (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value)
  );
}

export function isLocalized(
  value: JsonValue
): value is { en: string; tr: string } {
  return (
    isPlainObject(value) &&
    typeof value.en === "string" &&
    typeof value.tr === "string" &&
    Object.keys(value).every((key) => key === "en" || key === "tr")
  );
}

export function pathLabel(path: JsonPath): string {
  if (!path.length) return "Content";
  return path
    .map((part) => (typeof part === "number" ? `[${part + 1}]` : part))
    .join(" › ")
    .replace(/ › \[/g, " [");
}

export function humanizeKey(key: string | number): string {
  if (typeof key === "number") return `Item ${key + 1}`;
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());
}

export function getAtPath(root: JsonValue, path: JsonPath): JsonValue | undefined {
  let current: JsonValue | undefined = root;
  for (const segment of path) {
    if (typeof segment === "number") {
      if (!Array.isArray(current)) return undefined;
      current = current[segment];
    } else {
      if (!isPlainObject(current)) return undefined;
      current = current[segment];
    }
  }
  return current;
}

export function updateAtPath(
  root: JsonValue,
  path: JsonPath,
  nextValue: JsonValue
): JsonValue {
  if (!path.length) return nextValue;
  const [head, ...rest] = path;

  if (typeof head === "number") {
    if (!Array.isArray(root)) return root;
    return root.map((item, index) =>
      index === head ? updateAtPath(item, rest, nextValue) : item
    );
  }

  if (!isPlainObject(root)) return root;
  return {
    ...root,
    [head]: updateAtPath(root[head], rest, nextValue)
  };
}

export function removeArrayItem(
  root: JsonValue,
  arrayPath: JsonPath,
  index: number
): JsonValue {
  const array = getAtPath(root, arrayPath);
  if (!Array.isArray(array) || index < 0 || index >= array.length) return root;
  return updateAtPath(
    root,
    arrayPath,
    array.filter((_, itemIndex) => itemIndex !== index)
  );
}

export function moveArrayItem(
  root: JsonValue,
  arrayPath: JsonPath,
  from: number,
  to: number
): JsonValue {
  const array = getAtPath(root, arrayPath);
  if (
    !Array.isArray(array) ||
    from < 0 ||
    to < 0 ||
    from >= array.length ||
    to >= array.length ||
    from === to
  ) {
    return root;
  }
  const next = [...array];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return updateAtPath(root, arrayPath, next);
}

export function appendArrayItem(
  root: JsonValue,
  arrayPath: JsonPath,
  item: JsonValue
): JsonValue {
  const array = getAtPath(root, arrayPath);
  if (!Array.isArray(array)) return root;
  return updateAtPath(root, arrayPath, [...array, item]);
}

export function blankFromTemplate(value: JsonValue): JsonValue {
  if (isLocalized(value)) return { en: "", tr: "" };
  if (Array.isArray(value)) return [];
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [
        key,
        isBrandKey(key) ? brandValueFor(child) : blankFromTemplate(child)
      ])
    );
  }
  if (typeof value === "string") return "";
  if (typeof value === "number") return 0;
  if (typeof value === "boolean") return false;
  return null;
}

export function isBrandKey(key: string | number): boolean {
  if (typeof key === "number") return false;
  return BRAND_KEYS.has(key.replace(/[^a-z0-9]/gi, "").toLowerCase());
}

function brandValueFor(value: JsonValue): JsonValue {
  if (isLocalized(value)) return { en: "ATALAY", tr: "ATALAY" };
  return "ATALAY";
}

export function enforceAtalayBrand(value: JsonValue): JsonValue {
  if (Array.isArray(value)) return value.map(enforceAtalayBrand);
  if (!isPlainObject(value)) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [
      key,
      isBrandKey(key) ? brandValueFor(child) : enforceAtalayBrand(child)
    ])
  );
}

export function findProhibitedTerms(value: JsonValue): ContentWarning[] {
  const results: ContentWarning[] = [];

  function visit(current: JsonValue, path: JsonPath) {
    if (typeof current === "string") {
      const lower = current.toLocaleLowerCase("tr-TR");
      for (const term of PROHIBITED_TERMS) {
        if (lower.includes(term.toLocaleLowerCase("tr-TR"))) {
          results.push({
            path: pathLabel(path),
            term,
            excerpt:
              current.length > 100 ? `${current.slice(0, 97).trim()}…` : current
          });
        }
      }
      return;
    }
    if (Array.isArray(current)) {
      current.forEach((child, index) => visit(child, [...path, index]));
      return;
    }
    if (isPlainObject(current)) {
      Object.entries(current).forEach(([key, child]) =>
        visit(child, [...path, key])
      );
    }
  }

  visit(value, []);
  return results;
}

export function contentMatchesSearch(
  value: JsonValue,
  path: JsonPath,
  search: string
): boolean {
  const needle = search.trim().toLocaleLowerCase("tr-TR");
  if (!needle) return true;
  if (pathLabel(path).toLocaleLowerCase("tr-TR").includes(needle)) return true;
  if (typeof value === "string") {
    return value.toLocaleLowerCase("tr-TR").includes(needle);
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value).includes(needle);
  }
  if (Array.isArray(value)) {
    return value.some((child, index) =>
      contentMatchesSearch(child, [...path, index], search)
    );
  }
  if (isPlainObject(value)) {
    return Object.entries(value).some(([key, child]) =>
      contentMatchesSearch(child, [...path, key], search)
    );
  }
  return false;
}

export function stableJson(value: JsonValue): string {
  return JSON.stringify(value);
}
